// ── StateTransitionButtons ────────────────────────────────────────────────────
// Renders the correct action buttons based on assignment status + actor role.
// Sprint H: 8-state unified lifecycle per D-SH-4.
// H-6 wires onBeforeApprove and onBeforeSentBack modals.

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTransitionAssignment } from './api';
import type { Assignment, AssignmentState } from '@grc/shared';

const TERMINAL_STATES: ReadonlySet<AssignmentState> = new Set([
  'implemented', 'compensating_control', 'not_applicable',
]);

interface Props {
  assignment: Assignment;
  currentUserId: string;
  isCiso: boolean;
  controlId: string;
  onTransitioned?: () => void;
  onBeforeSubmit?: () => Promise<void> | void;
  onBeforeApprove?: () => Promise<void> | void;  // H-6: outcome picker for verifier
  onBeforeSentBack?: () => Promise<void> | void; // H-6: sent-back comment form
}

type ButtonAction = 'direct' | 'submit_modal' | 'approve_modal' | 'sent_back_modal';

interface TransitionAction {
  label: string;
  to: AssignmentState;
  variant?: 'default' | 'outline' | 'ghost';
  action: ButtonAction;
}

function getActions(
  status: AssignmentState,
  currentUserId: string,
  isCiso: boolean,
  assigneeUserId: string | null,
  verifierUserId: string | null,
): TransitionAction[] {
  const isAssignee = currentUserId === assigneeUserId;
  const isVerifier = currentUserId === verifierUserId;
  const actions: TransitionAction[] = [];

  switch (status) {
    case 'assigned':
      if (isAssignee || isCiso) {
        actions.push({ label: 'Start Work', to: 'in_progress', action: 'direct' });
      }
      break;

    case 'in_progress':
      if (isAssignee || isCiso) {
        actions.push({ label: 'Submit for Review', to: 'submitted', action: 'submit_modal' });
      }
      break;

    case 'submitted':
      if (isVerifier || isCiso) {
        actions.push({ label: 'Approve', to: 'implemented', action: 'approve_modal', variant: 'default' });
        actions.push({ label: 'Send Back', to: 'sent_back', action: 'sent_back_modal', variant: 'outline' });
      }
      break;

    case 'sent_back':
      if (isAssignee || isCiso) {
        actions.push({ label: 'Resume Work', to: 'in_progress', action: 'direct' });
      }
      break;

    default:
      if (TERMINAL_STATES.has(status) && isCiso) {
        actions.push({ label: 'Reopen', to: 'in_progress', action: 'direct', variant: 'outline' });
      }
  }

  return actions;
}

export function StateTransitionButtons({
  assignment,
  currentUserId,
  isCiso,
  controlId,
  onTransitioned,
  onBeforeSubmit,
  onBeforeApprove,
  onBeforeSentBack,
}: Props) {
  const transitionMutation = useTransitionAssignment(controlId);
  const [isIntercepting, setIsIntercepting] = useState(false);

  const actions = getActions(
    assignment.status,
    currentUserId,
    isCiso,
    assignment.assignee_user_id,
    assignment.verifier_user_id,
  );

  if (actions.length === 0) return null;

  async function handleClick(action: TransitionAction) {
    if (action.action === 'submit_modal' && onBeforeSubmit) {
      setIsIntercepting(true);
      try { await onBeforeSubmit(); } finally { setIsIntercepting(false); }
      return;
    }
    if (action.action === 'approve_modal' && onBeforeApprove) {
      setIsIntercepting(true);
      try { await onBeforeApprove(); } finally { setIsIntercepting(false); }
      return;
    }
    if (action.action === 'sent_back_modal' && onBeforeSentBack) {
      setIsIntercepting(true);
      try { await onBeforeSentBack(); } finally { setIsIntercepting(false); }
      return;
    }

    // Direct transition (no modal required, or modal not wired yet)
    // Note: approve_modal and sent_back_modal without their callbacks are intentionally
    // suppressed — transitions requiring modals must not fire without them.
    if (action.action === 'approve_modal' || action.action === 'sent_back_modal') return;

    transitionMutation.mutate(
      { id: assignment.id, to: action.to },
      {
        onSuccess: () => {
          toast.success(`Status moved to ${action.to.replace(/_/g, ' ')}`);
          onTransitioned?.();
        },
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { errors?: { message?: string }[] } } })
            ?.response?.data?.errors?.[0]?.message ?? 'Transition failed';
          toast.error(msg);
        },
      },
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {actions.map((action) => (
        <Button
          key={action.to}
          variant={action.variant ?? 'default'}
          size="sm"
          disabled={transitionMutation.isPending || isIntercepting}
          onClick={() => handleClick(action)}
        >
          {action.label}
        </Button>
      ))}
    </div>
  );
}
